import * as THREE from 'three';
import { Octree } from 'three/addons/math/Octree.js';
import { Capsule } from 'three/addons/math/Capsule.js';

const _probeFrom = new THREE.Vector3();
const _probeDir = new THREE.Vector3();

const _v = new THREE.Vector3();
const _v2 = new THREE.Vector3();

/**
 * Broadphase + narrowphase for level collision. The level is static, so it is
 * baked once into an Octree of triangles; dynamic bodies are capsules/spheres.
 */
const _c = new THREE.Vector3();
const _p = new THREE.Vector3();
const _q = new THREE.Vector3();
/** Constant, so the squeeze escape allocates nothing per frame. */
const _upNormal = new THREE.Vector3(0, 1, 0);
const _n = new THREE.Vector3();
const _seg = new THREE.Vector3();

/** Closest point on segment ab to point p, written into `out`. */
function closestOnSegment(a, b, p, out) {
  _seg.subVectors(b, a);
  const len2 = _seg.lengthSq();
  if (len2 < 1e-12) return out.copy(a);
  let t = _seg.dot(_v.subVectors(p, a)) / len2;
  t = Math.max(0, Math.min(1, t));
  return out.copy(a).addScaledVector(_seg, t);
}

/**
 * Octree subdivision cap, shared by BOTH build paths.
 *
 * It must be identical on client and server. Capsule resolution is
 * order-dependent — resolving against triangle A then B does not give the same
 * result as B then A — and the tree's depth decides that order. Building the
 * client at three.js's default depth 16 and the server at 6 made the two
 * simulations drift up to 156 mm apart over ten seconds, which prediction would
 * have spent the whole match fighting.
 *
 * 6 is also simply better than 16 on this geometry: fewer references, less
 * memory, faster build AND faster queries. See buildFromTriangles.
 */
export const OCTREE_MAX_LEVEL = 6;

/** Build an Octree with the shared depth cap applied. */
function buildCapped(octree, maxLevel = OCTREE_MAX_LEVEL) {
  // Patched on the prototype rather than by subclassing: split() constructs its
  // children as plain Octrees, so a subclass would lose the cap one level down.
  // Single-threaded and synchronous, restored in finally.
  const proto = Object.getPrototypeOf(octree);
  const origSplit = proto.split;
  proto.split = function cappedSplit(level) {
    if (level >= maxLevel) return;
    return origSplit.call(this, level);
  };
  try {
    octree.build();
  } finally {
    proto.split = origSplit;
  }
  return octree;
}

export class Physics {
  constructor() {
    /**
     * The map's own standing-player collision hull, when it has one.
     *
     * Never used to resolve movement — it is dilated by the player's size, so
     * collisions against it would make every wall 0.4 m thick. It answers one
     * question, after the fact: has somebody ended up where the level forbade.
     * See src/physics/ClipHull.js.
     */
    this.clipHull = null;
    this.world = new Octree();
    this.gravity = 24;
    this.bodies = [];
    this.built = false;
    /**
     * Movers: doors, lifts and any other brush that has to collide while it is
     * animating. The static world lives in an Octree that is far too expensive
     * to rebuild per frame, so these are kept as AABBs and resolved separately.
     * Each carries the delta it moved this frame so anything standing on it can
     * be carried along.
     */
    this.movers = [];
    /** Climbable volumes (AABBs). Overlap puts the player into ladder mode. */
    this.ladders = [];
  }

  addMover(mover) { this.movers.push(mover); return mover; }
  addLadder(box) { this.ladders.push(box); return box; }

  /** True if the capsule currently intersects static world geometry or a mover. */
  overlaps(capsule) {
    if (this.world.capsuleIntersect(capsule)) return true;
    for (const m of this.movers) {
      if (!m.solid) continue;
      // Broad phase on the union, then the parts. See collideCapsule for why a
      // mover is not one box.
      closestOnSegment(capsule.start, capsule.end, m.box.getCenter(_c), _p);
      m.box.clampPoint(_p, _q);
      if (_p.distanceToSquared(_q) >= capsule.radius * capsule.radius) continue;
      for (const part of m.parts ?? [m.box]) {
        closestOnSegment(capsule.start, capsule.end, part.getCenter(_c), _p);
        part.clampPoint(_p, _q);
        if (_p.distanceToSquared(_q) < capsule.radius * capsule.radius) return true;
      }
    }
    return false;
  }

  /** True when a capsule overlaps any ladder volume. */
  onLadder(capsule) {
    for (const box of this.ladders) {
      closestOnSegment(capsule.start, capsule.end, box.getCenter(_c), _p);
      box.clampPoint(_p, _q);
      // Generous horizontally: you should grab a ladder you are near, not only
      // one you are inside.
      if (_p.distanceToSquared(_q) < (capsule.radius + 0.22) ** 2) return true;
    }
    return false;
  }

  /**
   * Capsule vs a single AABB. Returns the contact normal, or null.
   * Mutates the capsule to push it out of penetration.
   */
  _resolveBox(capsule, box, velocity) {
    closestOnSegment(capsule.start, capsule.end, box.getCenter(_c), _p);
    box.clampPoint(_p, _q);
    const d2 = _p.distanceToSquared(_q);
    const r = capsule.radius;

    if (d2 > r * r) return null;

    if (d2 > 1e-8) {
      const dist = Math.sqrt(d2);
      _n.subVectors(_p, _q).divideScalar(dist);
      capsule.translate(_v.copy(_n).multiplyScalar(r - dist));
    } else {
      // Centre is inside the box: escape along the axis of least penetration.
      const dx = Math.min(_p.x - box.min.x, box.max.x - _p.x);
      const dy = Math.min(_p.y - box.min.y, box.max.y - _p.y);
      const dz = Math.min(_p.z - box.min.z, box.max.z - _p.z);
      const m = Math.min(dx, dy, dz);
      _n.set(0, 0, 0);
      if (m === dx) _n.x = _p.x - box.getCenter(_c).x > 0 ? 1 : -1;
      else if (m === dy) _n.y = _p.y - box.getCenter(_c).y > 0 ? 1 : -1;
      else _n.z = _p.z - box.getCenter(_c).z > 0 ? 1 : -1;
      capsule.translate(_v.copy(_n).multiplyScalar(m + r));
    }

    const into = _n.dot(velocity);
    if (into < 0) velocity.addScaledVector(_n, -into);
    return _n.clone();
  }

  /** Bake a static collision world from a THREE.Object3D hierarchy. */
  build(root) {
    this.world = new Octree();
    // fromGraphNode() collects the triangles and then builds; the build has to
    // be the CAPPED one so the client's world is identical to the server's.
    for (const child of [root]) this._collectInto(this.world, child);
    buildCapped(this.world);
    this.built = true;
    this._measureBounds();
  }

  /** Walk a scene graph adding world-space triangles, as fromGraphNode does. */
  _collectInto(octree, root) {
    root.updateWorldMatrix(true, true);
    root.traverse((o) => {
      if (!o.isMesh || !o.geometry) return;
      const geo = o.geometry;
      const pos = geo.attributes.position;
      if (!pos) return;
      const idx = geo.index;
      const n = idx ? idx.count : pos.count;
      const a = new THREE.Vector3(), b = new THREE.Vector3(), c = new THREE.Vector3();
      for (let i = 0; i < n; i += 3) {
        const i0 = idx ? idx.getX(i) : i;
        const i1 = idx ? idx.getX(i + 1) : i + 1;
        const i2 = idx ? idx.getX(i + 2) : i + 2;
        a.fromBufferAttribute(pos, i0).applyMatrix4(o.matrixWorld);
        b.fromBufferAttribute(pos, i1).applyMatrix4(o.matrixWorld);
        c.fromBufferAttribute(pos, i2).applyMatrix4(o.matrixWorld);
        octree.addTriangle(new THREE.Triangle(a.clone(), b.clone(), c.clone()));
      }
    });
  }

  /**
   * Bake the same world from raw triangles instead of a scene graph.
   *
   * This is what lets the game server exist. `build()` needs a THREE.Object3D
   * hierarchy, which on three of the four maps means a renderer, a texture
   * factory and a WebGL context — none of which a headless Node process has.
   * The triangles are extracted once by tools/export-collision.mjs and shipped
   * as data, so the server loads collision without ever constructing a mesh.
   *
   * @param {Float32Array|number[]} pos  flat xyz triplets, 9 numbers per triangle
   * @param {number} maxLevel  octree subdivision cap; see the note in the body
   */
  buildFromTriangles(pos, maxLevel = OCTREE_MAX_LEVEL) {
    this.world = new Octree();
    const a = new THREE.Vector3(), b = new THREE.Vector3(), c = new THREE.Vector3();
    for (let i = 0; i < pos.length; i += 9) {
      a.set(pos[i], pos[i + 1], pos[i + 2]);
      b.set(pos[i + 3], pos[i + 4], pos[i + 5]);
      c.set(pos[i + 6], pos[i + 7], pos[i + 8]);
      this.world.addTriangle(new THREE.Triangle(a.clone(), b.clone(), c.clone()));
    }
    // Octree.split() keeps subdividing while a cell holds more than 8 triangles,
    // to a depth of 16. That is tuned for a browser with gigabytes to spare. On
    // a 1 vCPU / 958 MB VPS that also has to keep a VLESS proxy alive it is
    // dangerous: Crossfire's 11.5k collision triangles exploded into 1.16M
    // octree references and 388 MB RSS, because a large floor triangle is
    // duplicated into every cell it spans.
    //
    // Measured on Crossfire, depth 16 vs 6:
    //   references  946k -> 122k
    //   heap        251 MB -> 30 MB
    //   build       875 ms -> 144 ms
    //   2k raycasts   9 ms -> 3 ms
    // Faster on every axis, not a trade — past a point the deeper tree costs
    // more to walk than the triangles it saves testing.
    //
    buildCapped(this.world, maxLevel);
    this.built = true;
    this._measureBounds();
    return this;
  }

  /**
   * The extent of the baked world, and the height below which you are no longer
   * in it.
   *
   * Both maps converted from GoldSrc lost their CLIP brushes — the invisible
   * volumes the original level designers used to stop players climbing onto
   * roofs — because a clip brush is textured `CLIP` and the converter drops
   * marker textures. The roofs are therefore reachable, there is no floor
   * beyond the outer walls, and running off one drops you forever. The clip
   * brushes are not recoverable from the payloads we ship, so the world gets a
   * bottom instead: see PlayerController's out-of-bounds recovery.
   *
   * The threshold is 3 m below the LOWEST TRIANGLE IN THE WORLD, which is not
   * an arbitrary margin: by construction there is no geometry beneath it, so
   * anything down there is falling through nothing and no legitimate pit,
   * tunnel or sewer can be caught by it. Measured on Dust II, 147 of the 173
   * ways out of the world are interior voids rather than the outer edge, so a
   * perimeter wall would not have been the fix.
   */
  _measureBounds() {
    const box = new THREE.Box3();
    const walk = (n) => {
      for (const t of n.triangles ?? []) {
        box.expandByPoint(t.a); box.expandByPoint(t.b); box.expandByPoint(t.c);
      }
      for (const s of n.subTrees ?? []) walk(s);
    };
    walk(this.world);
    this.bounds = box.isEmpty() ? null : box;
    this.killY = this.bounds ? this.bounds.min.y - 3 : null;
    return this.bounds;
  }

  /**
   * Triangle count in the baked world — a cheap sanity check that a map loaded.
   * Counted recursively: Octree.build() pushes triangles down into subTrees, so
   * the root's own array is empty on anything but a trivial world.
   */
  get triangleCount() {
    const walk = (n) => (n.triangles?.length ?? 0)
      + (n.subTrees ?? []).reduce((s, c) => s + walk(c), 0);
    return walk(this.world);
  }

  /** Horizontal probe axes for `insideSolid`. Module-level, never allocated. */
  static PROBE_DIRS = [
    new THREE.Vector3(1, 0, 0), new THREE.Vector3(-1, 0, 0),
    new THREE.Vector3(0, 0, 1), new THREE.Vector3(0, 0, -1),
  ];

  raycast(origin, direction, far = 500) {
    const ray = new THREE.Ray(origin.clone(), direction.clone().normalize());
    const hit = this.world.rayIntersect(ray);
    if (!hit || hit.distance > far) return null;
    // Octree.rayIntersect gives us the triangle, not a normal — derive it and
    // flip it to face the ray so impact FX always spawn on the visible side.
    const normal = hit.triangle.getNormal(new THREE.Vector3());
    if (normal.dot(ray.direction) > 0) normal.negate();
    return { point: hit.position.clone(), normal, distance: hit.distance };
  }

  /**
   * Is this point INSIDE solid world geometry?
   *
   * ## Why this needs its own function
   *
   * `Octree.rayIntersect` culls backfaces — `ray.intersectTriangle(a, b, c,
   * true, _v1)` in the three.js addon. So a ray that starts inside a brush and
   * travels out reports NOTHING: the only triangle on its path is the far
   * face, seen from behind. `raycast` from an embedded point therefore returns
   * "clear" in every direction, which is why an embedded bot believed it had
   * line of sight to everything while the player's rounds — travelling the
   * other way, into a frontface — stopped dead on the wall.
   *
   * The test exploits exactly that asymmetry instead of fighting it. For an
   * axis d: cast out from `p`, and cast back from `p + d * reach`. If the
   * inbound ray hits something before it reaches `p` while the outbound ray saw
   * nothing, there is a surface between them facing AWAY from `p` — so `p` is
   * behind it, i.e. inside the solid. One axis showing that is enough; a wall
   * is one axis.
   *
   * Horizontal axes only. A point standing on a floor is legitimately "behind"
   * the floor surface looking down, and a low ceiling would do the same
   * overhead, so testing Y would call every crouched bot embedded.
   *
   * @param {THREE.Vector3} p
   * @param {number} reach how far out to look for the enclosing face
   */
  insideSolid(p, reach = 1.2) {
    for (const d of Physics.PROBE_DIRS) {
      if (this.raycast(p, d, reach)) continue;          // frontface out: open
      _probeFrom.copy(p).addScaledVector(d, reach);
      _probeDir.copy(d).negate();
      const back = this.raycast(_probeFrom, _probeDir, reach - 0.02);
      if (back) return true;
    }
    return false;
  }

  /**
   * Resolve a capsule against the static world.
   * Returns { grounded, normal } — velocity is mutated in place.
   */
  collideCapsule(capsule, velocity, damping = 0) {
    let grounded = false;
    let normal = null;
    let platform = null;

    /*
     * Movers first: if a lift is rising into the player, resolving it before
     * the static world lets the world push back and stops the player being
     * driven through the floor.
     *
     * Against the mover's PARTS, not its bounding box. A lift is a piston and a
     * platform, and their union is a solid block four metres tall of which 85%
     * is air — so the space under a raised platform collided, and a descending
     * lift engulfed anyone standing under it rather than landing on them. Doors
     * are single slabs and are unaffected either way.
     */
    for (const m of this.movers) {
      if (!m.solid) continue;
      // Cheap reject on the union before touching the parts.
      closestOnSegment(capsule.start, capsule.end, m.box.getCenter(_c), _p);
      m.box.clampPoint(_p, _q);
      if (_p.distanceToSquared(_q) >= capsule.radius * capsule.radius) continue;
      for (const part of m.parts ?? [m.box]) {
        const n = this._resolveBox(capsule, part, velocity);
        if (!n) continue;
        if (n.y > 0.45) {
          grounded = true;
          platform = m;
          if (velocity.y < 0) velocity.y = 0;
        }
        normal = n;
      }
    }

    const result = this.world.capsuleIntersect(capsule);

    if (result) {
      // Don't clear a mover contact: standing on a lift while brushing a wall
      // must stay grounded, or the player jitters off the platform.
      if (result.normal.y > 0.45) {
        grounded = true;
        if (velocity.y < 0) velocity.y = 0;
      } else {
        // Slide: remove the component of velocity heading into the surface.
        const into = result.normal.dot(velocity);
        if (into < 0) velocity.addScaledVector(result.normal, -into);
      }
      normal = result.normal.clone();
      capsule.translate(result.normal.multiplyScalar(result.depth));
    }

    /*
     * Squeezed between a mover and the world: escape upward.
     *
     * A descending platform pushes you DOWN, which is right until the floor is
     * already under your boots — then there is nowhere to go, and the plate
     * (0.41 m thin, thinner than a player) walks straight through you. Traced
     * on Crossfire's lifts: the plate spent six frames inside the capsule
     * before the least-penetration axis flipped and popped the player out on
     * top. Six frames at 5 m/s is a visible clip through somebody's chest, and
     * it is the other half of "you get stuck in them".
     *
     * Standing them on top immediately is what the flip eventually did anyway,
     * and it is what every engine does with a door that cannot push you: the
     * squeeze resolves along the one axis that is still free. There is no crush
     * damage in this game, so being lifted is the whole outcome.
     */
    for (const m of this.movers) {
      // Only a mover on its way DOWN, and only while it is moving. That is the
      // one case with no other answer, and confining the rule to it leaves
      // every door, button and stationary platform behaving exactly as before —
      // a player brushing a sliding door still gets pushed aside, not stood on
      // top of it.
      if (!m.solid || !(m.delta?.y < -1e-4)) continue;
      for (const part of m.parts ?? [m.box]) {
        closestOnSegment(capsule.start, capsule.end, part.getCenter(_c), _p);
        part.clampPoint(_p, _q);
        if (_p.distanceToSquared(_q) >= capsule.radius * capsule.radius) continue;
        const rise = part.max.y + capsule.radius - capsule.start.y;
        /*
         * Up onto it, as long as "it" is something a person could be standing
         * under — its top no higher than a body length above their feet.
         *
         * The limit has to cover the FIRST frame of contact, not the frame
         * where the plate has already sunk to knee height. Traced on lift1: the
         * plate's underside reaches the top of the head while its top is still
         * 1.76 m above the capsule origin, and a limit of 1.68 waited six more
         * frames — by which point 37 cm of head was inside it. Firing at first
         * contact is a snap upward of about a body height; leaving it is a
         * platform passing visibly through someone's chest, which is what was
         * reported.
         */
        const reach = (capsule.end.y - capsule.start.y) + capsule.radius * 2 + 0.4;
        if (rise <= 0 || rise > reach) continue;
        capsule.translate(_v.set(0, rise, 0));
        if (velocity.y < 0) velocity.y = 0;
        grounded = true;
        platform = m;
        normal = _upNormal.clone();
      }
    }

    if (damping) velocity.multiplyScalar(1 - damping);
    return { grounded, normal, platform };
  }

  /** Simple sphere body used for shells, gibs and grenades. */
  addBody(body) { this.bodies.push(body); return body; }
  removeBody(body) {
    const i = this.bodies.indexOf(body);
    if (i >= 0) this.bodies.splice(i, 1);
  }

  step(dt) {
    for (let i = this.bodies.length - 1; i >= 0; i--) {
      const b = this.bodies[i];
      b.life -= dt;
      if (b.life <= 0) { this.bodies.splice(i, 1); b.onExpire?.(); continue; }

      b.velocity.y -= this.gravity * dt;
      b.velocity.multiplyScalar(Math.max(0, 1 - b.drag * dt));
      _v.copy(b.velocity).multiplyScalar(dt);
      b.position.add(_v);

      const sphere = new THREE.Sphere(b.position, b.radius);
      const hit = this.world.sphereIntersect(sphere);
      if (hit) {
        b.position.addScaledVector(hit.normal, hit.depth);
        const vn = hit.normal.dot(b.velocity);
        if (vn < 0) {
          // Reflect with restitution, then shed tangential energy (friction).
          _v2.copy(hit.normal).multiplyScalar(vn);
          b.velocity.addScaledVector(_v2, -(1 + b.restitution));
          b.velocity.multiplyScalar(0.82);
          b.onBounce?.(hit.normal, Math.abs(vn));
        }
      }

      b.spin && b.quaternion?.multiply(
        new THREE.Quaternion().setFromAxisAngle(b.spin.axis, b.spin.rate * dt)
      );
    }
  }
}

export { Capsule };
